/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useEffect, useRef, useState} from 'react'
import {useLocation} from 'react-router-dom'
import {Helmet} from 'react-helmet'

// Components
import {Box} from '@salesforce/retail-react-app/app/components/shared/ui'

// Project Components
import Seo from '../../components/seo'
import PDComponent from '../../components/page-designer/pd-component'

//Hooks
import useEinstein from '@salesforce/retail-react-app/app/hooks/use-einstein'
import {useCustomerId, useCustomerOrders, useCustomerType} from '@salesforce/commerce-sdk-react'

// Constants
import {
    YR_HOME_HREF,
    KS_HOME_HREF,
    MAX_CACHE_AGE,
    STALE_WHILE_REVALIDATE,
    PD_HOMEPAGE_YR,
    PD_HOMEPAGE_KS
} from '../../constants'

import {useServerContext} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'
import {CookieStorage} from '@salesforce/commerce-sdk-react/auth/storage'
import {onClient} from '@salesforce/commerce-sdk-react/utils'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import useTagManager from '../../hooks/use-tag-manager'
import useMonetate from '../../hooks/use-monetate'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import {useSearchParams} from '@salesforce/retail-react-app/app/hooks'
import {useWishList} from '../../hooks/use-wish-list'
import {getTotalQuantity} from '../../utils/basket-utils'
import {useSiteContext} from '../../contexts/site'
import {useGlobaleContext} from '../../contexts/globale'
import usePageDesigner from '../../hooks/use-page-designer'
import SchemaMarkup from './partials/schema-markup'
import EspSkeleton from './partials/skeleton'
import useCustomerGroups from '../../hooks/use-customer-groups'

/**
 * This is the home page for Retail React App.
 * The page is created for demonstration purposes.
 * The page renders SEO metadata and a few promotion
 * categories and products, data is from local file.
 */
const Home = () => {
    const {isYR} = useSiteContext()
    const einstein = useEinstein()
    const {pathname, hash} = useLocation()
    const tagManager = useTagManager()
    const monetate = useMonetate()
    const {data: customerGroupIds} = useCustomerGroups()
    const {
        app: {commerceAPI}
    } = getConfig()
    const title = isYR
        ? 'Yellow Rose by Kendra Scott | Shop the Collection'
        : 'Kendra Scott Official | Jewelry, Personalized Gifts & Watches'
    const description = isYR
        ? 'Inspired by the Lone Star State, Yellow Rose by Kendra Scott is a unique collection that captures true southwestern style. Browse statement earrings, beaded bracelets, pendant necklaces & more.'
        : 'Find limited-edition jewelry for every look with simple pendant necklaces, huggie earrings, band rings, and more.'

    const {countryISO, currencyCode} = useGlobaleContext()

    const {isRegistered, customerType} = useCustomerType()
    const {data: customer} = useCurrentCustomer()
    const customerId = useCustomerId()
    const {data: basket, isLoading: isBasketLoading} = useCurrentBasket()

    const searchParams = useSearchParams({limit: 100, offset: 0})
    const {data: wishlist, isLoading: isWishlistLoading} = useWishList()
    const {limit, offset} = searchParams[0]
    const {data: {data: orders} = {}, isLoading: isOrdersLoading} = useCustomerOrders(
        {
            parameters: {customerId, limit, offset}
        },
        {enabled: onClient() && !!customerId && customer?.customerType === 'registered'}
    )
    const cookie =
        onClient() &&
        new CookieStorage({
            keySuffix: '',
            sharedContext: !onClient()
        })

    const {changeUp} = commerceAPI
    const {configChangeup} = changeUp || {}

    var changeUpProductID = null

    try {
        const jsonConfig = JSON.parse(configChangeup)
        changeUpProductID = jsonConfig.enabled ? jsonConfig.productID : null
    } catch (e) {
        console.log(e.message)
    }

    const totalQuantity = getTotalQuantity(basket, changeUpProductID)
    const [globalDataLoaded, setGlobalDataLayerLoaded] = useState(false)
    const debounceRef = useRef(null)

    useEffect(() => {
        const city = cookie.get('city')
        const country = cookie.get('countryCode') || 'US'
        const state = cookie.get('state')
        if (!country) return

        if (
            ((customer?.customerType === 'registered' && customer?.email) ||
                customer?.customerType === 'guest') &&
            !isBasketLoading &&
            !globalDataLoaded
        ) {
            if (debounceRef.current) clearTimeout(debounceRef.current)

            debounceRef.current = setTimeout(() => {
                tagManager.globalDataLoad(
                    'main',
                    'home',
                    totalQuantity,
                    basket?.productTotal,
                    customer,
                    city,
                    country,
                    state
                )
                setGlobalDataLayerLoaded(true)
            }, 2000)
        }
    }, [customer?.customerType, customer?.email, isBasketLoading, cookie])

    useEffect(() => {
        setGlobalDataLayerLoaded(false)
    }, [pathname])
    const customerClientId = useCustomerId()
    useEffect(() => {
        if (!!customerClientId && customer?.customerType === 'registered' && isOrdersLoading) {
            return
        }
        // 'CookieStorage' takes some miliseconds to fetch cookies, that's why we've used 'useEffect'
        const city = cookie.get('city')
        const country = cookie.get('countryCode') || 'US'
        const state = cookie.get('state')
        if (
            globalDataLoaded &&
            ((customer?.customerType === 'registered' && customer?.email) ||
                customer?.customerType === 'guest')
        ) {
            try {
                tagManager.dataLayerLoad(
                    'Home',
                    isRegistered,
                    'Home',
                    country || countryISO,
                    currencyCode,
                    customer,
                    city,
                    country,
                    state,
                    null,
                    null,
                    null,
                    orders?.length
                )
            } catch (e) {
                tagManager.dataLayerLoad(
                    'Home',
                    isRegistered,
                    'Home',
                    null,
                    null,
                    customer,
                    city,
                    country,
                    state,
                    null,
                    null,
                    null,
                    orders?.length
                )
            }
        }
    }, [globalDataLoaded, customer?.customerType, customer?.email, isOrdersLoading])

    useEffect(() => {
        if (
            (customer?.customerType === 'registered' && customer?.email) ||
            customer?.customerType === 'guest'
        ) {
            window.birthdayMonth = customer?.c_birthdayMonth
            window.monetatePhilantropyShopper = customer?.c_monetatePhilantropyShopper
        }
    }, [customer?.customerType, customer?.email])

    useEffect(() => {
        if (
            !isBasketLoading &&
            (!isOrdersLoading || customer?.customerType !== 'registered') &&
            !!customerType &&
            (!isWishlistLoading || customer?.customerType !== 'registered')
        ) {
            window.lastOrderDate = orders?.[0]?.creationDate || 'n/a'
            window.mtBreadcrumb = ['Home', title]
            monetate.monetateTrackPage(
                'main',
                ['Home', title],
                basket?.productItems || [],
                customer?.customerType === 'registered',
                wishlist?.customerProductListItems?.length || 0,
                null,
                null,
                null,
                basket?.giftCertificateItems
            )
            window.monetateRef = 'home'
        }
        // Safety checks to ensure scroll will work properly
        document.querySelector('html').style.overflow = ''
        document.querySelector('html').classList.remove('block-scroll')
    }, [isBasketLoading, isOrdersLoading, customerType, isWishlistLoading])

    // useServerContext is a special hook introduced in v3 PWA Kit SDK.
    // It replaces the legacy `getProps` and provide a react hook interface for SSR.
    // it returns the request and response objects on the server side,
    // and these objects are undefined on the client side.
    const {res} = useServerContext()
    if (res) {
        res.set(
            'Cache-Control',
            `s-maxage=${MAX_CACHE_AGE}, stale-while-revalidate=${STALE_WHILE_REVALIDATE}`
        )
    }

    const pageId = isYR ? PD_HOMEPAGE_YR : PD_HOMEPAGE_KS
    const {data: page, isLoading} = usePageDesigner(
        {pageId: pageId, customerGroupIds: customerGroupIds},
        {enabled: true}
    )
    /**************** Einstein ****************/
    useEffect(() => {
        einstein.sendViewPage(pathname)
    }, [])
    const {hostUrl} = commerceAPI.storefront

    useEffect(() => {
        if (isLoading) return

        const hash = window.location.hash
        if (!hash) return

        const el = document.getElementById(hash.slice(1))
        if (!el) return

        el.scrollIntoView({behavior: 'smooth'})
    }, [pageId, isLoading, hash])

    return (
        <Box data-testid="home-page">
            <Helmet>
                <link rel="canonical" href={hostUrl + (isYR ? YR_HOME_HREF : KS_HOME_HREF)}></link>
            </Helmet>
            {!isYR && <SchemaMarkup />}
            <Seo title={title} description={description} />
            {isLoading && !page && <EspSkeleton />}
            {!isLoading && page && <PDComponent page={page} parent={true} />}
        </Box>
    )
}

Home.getTemplateName = () => 'home'

export default Home
